'use client';
import type { FC } from 'react';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import type {
  CuratedTicketCategoryData,
  CuratedTicketEmbeddedWidgetData,
  CuratedTicketItem,
} from '@weberus/shared-types';
import { IcArrowforwardFill } from '@yanolja-rnd/nol-icons';
import { Thumbnail } from '@yanolja-rnd/nol-design-system';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { cn } from '@/app/_shared/utils/tw-cn';
import { BaseImage } from '@/app/_components/common/base-image';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs';
import { navigateTo } from '@/app/_shared/utils/action-navigator';

export interface CuratedTicketEmbeddedProps {
  data: CuratedTicketEmbeddedWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

const GradiantStyle =
  'rounded-[16px] p-2 bg-gradient-to-b from-[#4BA2F6] via-[#3C7BFD] via-[#3E5FEA] via-[#4052EB] to-[#643EDF] shadow-[0_0_8px_0_rgba(0,0,0,0.1)]';
/**
 * 놀 큐레이션 티켓 임베디드 위젯
 * @see {@link https://www.figma.com/design/fyzmsS9AvEfuKWScIr1CXc/-GUIDE--UPD-2057-%EC%9E%84%EB%B2%A0%EB%94%A9---%ED%99%88--%EC%A0%84%EC%B2%B4%EC%B9%B4%ED%85%8C%EA%B3%A0%EB%A6%AC?node-id=129-61120&m=dev}
 */
export const CuratedTicketEmbedded: FC<CuratedTicketEmbeddedProps> = ({ data }) => {
  const { header, categories, items } = data;

  const handleHeaderClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    await waitUntilClickTimeout(
      HomePageLog.CuratedTicketEmbeddedWidgetLabelNol.click({ logMeta: header.serverLogMeta }),
    );
    header.action && navigateTo(header.action);
  };
  const handleCategoryClick = async (e: React.MouseEvent, category: CuratedTicketCategoryData) => {
    e.preventDefault();
    await waitUntilClickTimeout(
      HomePageLog.CuratedTicketEmbeddedWidgetIconCategoryTitle.click({ logMeta: category.serverLogMeta }),
    );
    category.action && navigateTo(category.action);
  };

  const handleItemImpression = (item: CuratedTicketItem) => {
    clientLogProtobuf(HomePageLog.CuratedTicketEmbeddedWidgetCardId.impression, { logMeta: item.serverLogMeta })();
  };

  const handleItemClick = async (e: React.MouseEvent, item: CuratedTicketItem) => {
    e.preventDefault();
    await waitUntilClickTimeout(HomePageLog.CuratedTicketEmbeddedWidgetCardId.click({ logMeta: item.serverLogMeta }));
    item.action && navigateTo(item.action);
  };

  return (
    <div className="relative w-full px-16 pc:w-pc-size pc:px-0">
      {/* PC 용 */}
      <div className={cn('my-16 hidden h-[214px] shadow-2 pc:block pc:w-pc-size', GradiantStyle)}>
        <div className="flex h-full w-full flex-row rounded-[14px] bg-fill-neutral-weak p-24">
          {/* left area */}
          <div className="mr-24 flex h-full flex-col justify-center">
            {/* 헤더 영역 */}
            {header?.logoUrl && (
              <a
                // ref={ref}
                href={header.action.web}
                className="flex h-32 items-center"
                onClick={(e) => handleHeaderClick(e)}>
                <span className="relative h-32 w-96">
                  <BaseImage alt="NOL 티켓" src={header.logoUrl} />
                </span>
                <IcArrowforwardFill className="size-16 pc:size-20" />
              </a>
            )}
            {/* 카테고리 영역 */}
            <div className="mt-20 flex flex-row">
              {categories.map((category, index) => (
                <a
                  key={`category-${index}`}
                  href={category.action.web}
                  className="mr-8 flex items-center"
                  onClick={(e) => handleCategoryClick(e, category)}>
                  <span className="relative mr-5 size-24">
                    <BaseImage alt="" src={category.icon} />
                  </span>
                  <span className="typography-subtitle-16-regular">{category.title}</span>
                </a>
              ))}
            </div>
          </div>
          {/* right area */}
          <div className="grid h-full grid-cols-3 items-center justify-center gap-20">
            {/* 아이템 영역*/}
            {items.map((item, index) => (
              <YaTrackerImpression key={index} onImpressed={() => handleItemImpression(item)}>
                <a
                  key={`item-${index}`}
                  href={item.action.web}
                  className="flex h-[162px] w-[305px] flex-row items-center"
                  onClick={(e) => handleItemClick(e, item)}>
                  <Thumbnail
                    alt=""
                    image={item.image.url}
                    className="pc:w-[121px] pc:shrink-0"
                    shape="roundedRectangle_16"
                    ratio={{
                      mobile: '3_4',
                      pc: '3_4',
                    }}
                  />
                  <div className="ml-16 flex flex-col">
                    <span className="mb-8 typography-subtitle-18-bold-line-clamp-2">{item.title.value}</span>
                    <span className="typography-subtitle-16-regular">{item.location?.value}</span>
                  </div>
                </a>
              </YaTrackerImpression>
            ))}
          </div>
        </div>
      </div>
      {/* MW 용 */}
      <div className={cn('my-12 w-full items-center justify-center shadow-2 pc:hidden', GradiantStyle)}>
        <div className="flex h-full w-full flex-col rounded-[14px] bg-fill-neutral-weak p-16">
          {/* top area */}
          <div className="mb-16 flex h-full w-full flex-row">
            {/* 헤더 영역 */}
            {header?.logoUrl && (
              <a
                // ref={ref}
                href={header.action.web}
                className="mr-8 flex h-24 items-center"
                onClick={handleHeaderClick}>
                <span className="relative h-24 w-72">
                  <BaseImage alt="NOL 아이콘" src={header.logoUrl} />
                </span>
                <IcArrowforwardFill className="size-16 pc:size-20" />
              </a>
            )}
            {/* 카테고리 영역 */}
            <div className="grid grid-cols-3 gap-4">
              {categories.map((category, index) => (
                <a
                  key={`category-${index}`}
                  href={category.action.web}
                  className="mr-4 flex items-center"
                  onClick={(e) => handleCategoryClick(e, category)}>
                  <span className="relative mr-4 size-24">
                    <BaseImage alt={`${category.title} 아이콘`} src={category.icon} />
                  </span>
                  <span className="typography-body-13-regular">{category.title}</span>
                </a>
              ))}
            </div>
          </div>
          {/* bottom area*/}
          <div className="grid grid-cols-3 gap-8">
            {/* 아이템 영역*/}
            {items.map((item, index) => (
              <YaTrackerImpression key={index} onImpressed={() => handleItemImpression(item)}>
                <a
                  key={`item-${index}`}
                  href={item.action.web}
                  className="w-full pc:w-[100px]"
                  onClick={(e) => handleItemClick(e, item)}>
                  <Thumbnail
                    image={item.image.url}
                    className="w-full pc:w-[100px] pc:shrink-0"
                    shape="roundedRectangle_8"
                    ratio={{
                      mobile: '3_4',
                      pc: '3_4',
                    }}
                  />
                  <div className="mt-6 flex-row">
                    <p className="typography-body-12-regular-line-clamp-2">{item.title.value}</p>
                  </div>
                </a>
              </YaTrackerImpression>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
